import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'

import 'element-plus/dist/index.css'
import './styles/element-plus/theme.css'
import './styles/tailwind/index.css'
import './styles/element-plus/custom.css'
import './styles/main.css'

import App from './App.vue'
import router from './router'

import { abilitiesPlugin, ability } from './utils/plugins/casl'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(abilitiesPlugin, ability)
app.component('FontAwesomeIcon', FontAwesomeIcon)

/**
 * Vite only exposes `MODE`, `PROD`, `DEV` and `VITE_`-prefixed variables, so
 * `import.meta.env.NODE_ENV` was always undefined and every deployment sampled
 * at 100%. Keyed off `VITE_NODE_ENV` to match the existing check in TopBar.vue
 * and the `environment` tag passed to Sentry.init below.
 *
 * Tested for development rather than production on purpose: this app is
 * deployed to several staging and production environments, and an unset or
 * unrecognised value must fall back to the cheaper sample rate rather than
 * silently recording every session.
 */
const isDevelopment = import.meta.env.VITE_NODE_ENV === 'development'

if (import.meta.env.VITE_SENTRY_ENABLE === 'true') {
  const integrations = [
    Sentry.browserTracingIntegration({
      router // Vue Router tracing
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false
    }),
    // Add browser profiling integration to the list of integrations
    Sentry.browserProfilingIntegration()
  ]

  if (import.meta.env.VITE_SENTRY_FEEDBACK_ENABLE === 'true') {
    integrations.push(
      Sentry.feedbackIntegration({
        // Additional SDK configuration goes in here, for example:
        colorScheme: 'system'
      })
    )
  }

  Sentry.init({
    app: app,
    environment: import.meta.env.VITE_NODE_ENV,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations,
    // Performance Monitoring
    tracesSampleRate: isDevelopment ? 1 : 0.1,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    // Full capture locally, 10% everywhere that is deployed
    replaysSessionSampleRate: isDevelopment ? 1 : 0.1,
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })
}

// Handle preload error
window.addEventListener('vite:preloadError', () => {
  // console.log('New content available. Refreshing the page.')

  window.location.reload() // Refresh the page
})

app.mount('#app')
